'use client';

import { Button } from '@koda/koda-components/atoms/Button';
import { Card } from '@koda/koda-components/atoms/Card';
import ContentDialog from '@koda/koda-components/molecules/ContentDialog';
import { useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';
import { DRAMA_MAIL_BOX } from '../../../../../../src/lib/constants/mailBoxes';
import type { SanityLicense } from '../../types/licenses';
import { getLicenseKeyByCode, LicenseCode, LicenseTypes } from '../../types/licenses';

export type SummaryPanelProps = {
  license: SanityLicense;
  termsHeading: string;
  terms: ReactNode;
  pricesHeading: string;
  prices: ReactNode;
  fallback?: string;
  teaser?: ReactNode;
};

const abbreviations = ['TV'];

export const SummaryPanel = (props: SummaryPanelProps) => {
  const { license } = props;
  const t = useTranslations('licenses.basketPanel.summary');
  const normalizedLicenseName = (license?.name ?? '').trim();
  const isAbbreviation = abbreviations.includes(normalizedLicenseName.toUpperCase());
  const termsLicenseName = isAbbreviation ? normalizedLicenseName.toUpperCase() : normalizedLicenseName.toLowerCase();
  const licenseKey = getLicenseKeyByCode(license.licenseId as LicenseCode);
  if (!licenseKey) {
    throw new Error(`Invalid license code: ${license.licenseId}`);
  }
  const translationKey = licenseKey.charAt(0).toLowerCase() + licenseKey.slice(1);

  const [openPrices, setOpenPrices] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const isNotOtherLicenseType = props.license.licenseId !== LicenseTypes.OtherContactForm;

  const customerServiceLink = (chunks: ReactNode) => (
    <Button buttonType="link" variant="inlineLink" href={props.fallback}>
      {chunks}
    </Button>
  );

  const noticeKey = `${translationKey}.notice`;
  const notice = t.has(noticeKey)
    ? t.rich(noticeKey, {
        customerServiceLink: chunks => customerServiceLink(chunks),
      })
    : null;

  return (
    <>
      <Card
        aria-label={t('title')}
        flex={false}
        backgroundColor={'white'}
        hue={500}
        rounded={'lg'}
        className={'hidden md:block md:p-6'}
      >
        <div className="grid gap-6">
          <h2 className="text-lg font-semibold tracking-wide text-black-800">{t('title')}</h2>
          {isNotOtherLicenseType && (
            <dl className="flex w-full justify-between gap-2">
              <dt className="font-graphik text-md font-normal">{t('type')}</dt>
              <dd className="m-0 text-right font-graphik text-md font-medium">{license.name}</dd>
            </dl>
          )}
          <p className="hyphens-manual font-graphik text-md font-normal">
            {translationKey
              ? t.rich(`${translationKey}.description`, {
                  customerServiceLink: chunks => customerServiceLink(chunks),
                  lineBreak: () => (
                    <>
                      <br /> <br />
                    </>
                  ),
                  otherLineBreak: () => (
                    <>
                      <br /> <br />
                    </>
                  ),
                  // Only present in the theater license description.
                  mailTo: () => (
                    <a href={`mailto:${DRAMA_MAIL_BOX}`} className="text-primary underline">
                      {DRAMA_MAIL_BOX}
                    </a>
                  ),
                })
              : null}
          </p>
          {isNotOtherLicenseType && (
            <>
              {license.licenseId !== LicenseTypes.Theater && (
                <p className="hyphens-manual font-graphik text-md font-normal">{t('musicLicense.description')}</p>
              )}

              {notice ? <p className="hyphens-manual font-graphik text-md font-normal">{notice}</p> : null}

              <a
                data-testid="basket-open-terms"
                className="cursor-pointer text-primary underline"
                onClick={() => setOpenTerms(true)}
              >
                {`${t(`termsLinkText`)} ${termsLicenseName}`}
              </a>
              <a
                data-testid="basket-open-prices"
                className="cursor-pointer text-primary underline"
                onClick={() => setOpenPrices(true)}
              >
                {t(`priceCalcLinkText`)}
              </a>
            </>
          )}
        </div>
      </Card>
      <div className="flex flex-col items-center justify-center space-y-4 md:hidden">
        <Button testId="basket-open-prices" buttonType="link" variant="inlineLink" onClick={() => setOpenPrices(true)}>
          {t(`priceCalcLinkText`)}
        </Button>
        <Button testId="basket-open-terms" buttonType="link" variant="inlineLink" onClick={() => setOpenTerms(true)}>
          {`${t(`termsLinkText`)} ${termsLicenseName}`}
        </Button>
      </div>

      <ContentDialog
        open={openTerms}
        onOpenChange={setOpenTerms}
        content={props.terms}
        heading={props.termsHeading}
        footer={props.teaser}
      />
      <ContentDialog
        open={openPrices}
        onOpenChange={setOpenPrices}
        content={props.prices}
        heading={props.pricesHeading}
        footer={props.teaser}
      />
    </>
  );
};
